/**
 * TimelineCanvas Component
 * 
 * Main timeline view with horizontal scroll, recipe rows, and segments.
 */

import React, { useState, useRef, useEffect } from 'react';
import { RecipeWithTiming, ScheduledSegment, Conflict } from '../../types/menuPlanning';
import { calculateMakespan } from '../../utils/scheduler';
import TimeRuler from './TimeRuler';
import TrackRow from './TrackRow';
import ConflictBanner from './ConflictBanner';
import '../../styles/menuPlanning.css';

interface TimelineCanvasProps {
  recipes: RecipeWithTiming[];
  segments: ScheduledSegment[];
  conflicts: Conflict[];
  onSegmentUpdate?: (segmentId: string, newStartSec: number, newDurationSec: number) => void;
}

export const TimelineCanvas: React.FC<TimelineCanvasProps> = ({
  recipes,
  segments,
  conflicts,
  onSegmentUpdate
}) => {
  const [pixelsPerSecond, setPixelsPerSecond] = useState(2); // Initial zoom level
  const [showConflicts, setShowConflicts] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const makespanSec = calculateMakespan(segments);
  const minZoom = 0.5;
  const maxZoom = 10;

  const handleZoomIn = () => {
    setPixelsPerSecond(prev => Math.min(prev * 1.5, maxZoom));
  };

  const handleZoomOut = () => {
    setPixelsPerSecond(prev => Math.max(prev / 1.5, minZoom));
  };

  const handleSegmentDragEnd = (segment: ScheduledSegment, newStartSec: number) => {
    if (onSegmentUpdate) {
      onSegmentUpdate(segment.id, newStartSec, segment.durationSec);
    }
  };

  const handleSegmentResize = (segment: ScheduledSegment, newDurationSec: number) => {
    if (onSegmentUpdate) {
      onSegmentUpdate(segment.id, segment.startSec, newDurationSec);
    }
  };

  // Build set of conflicting segment IDs for highlighting
  const conflictSegmentIds = new Set<string>();
  for (const conflict of conflicts) {
    if (conflict.segment1?.id) conflictSegmentIds.add(conflict.segment1.id);
    if (conflict.segment2?.id) conflictSegmentIds.add(conflict.segment2.id);
  }

  if (recipes.length === 0 || segments.length === 0) {
    return (
      <div className="menu-planning-empty">
        <div className="menu-planning-empty-title">No Schedule Generated</div>
        <div className="menu-planning-empty-text">
          Select recipes and click "Generate Schedule" to see the timeline.
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-container" ref={containerRef}>
      {conflicts.length > 0 && showConflicts && (
        <ConflictBanner
          conflicts={conflicts}
          onClose={() => setShowConflicts(false)}
        />
      )}
      
      <div className="timeline-wrapper">
        <TimeRuler
          makespanSec={makespanSec}
          pixelsPerSecond={pixelsPerSecond}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          canZoomIn={pixelsPerSecond < maxZoom}
          canZoomOut={pixelsPerSecond > minZoom}
        />
        
        {recipes.map((recipe) => (
          <TrackRow
            key={recipe.id}
            recipe={recipe}
            segments={segments}
            pixelsPerSecond={pixelsPerSecond}
            conflictSegmentIds={conflictSegmentIds}
            onSegmentDragEnd={handleSegmentDragEnd}
            onSegmentResize={handleSegmentResize}
          />
        ))}
      </div>
    </div>
  );
};

export default TimelineCanvas;


